import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  User, 
  Heart, 
  MapPin, 
  Mail, 
  Instagram, 
  ExternalLink,
  Package,
  Sparkles,
  ArrowRight,
  CheckCircle
} from "lucide-react";
import { FaTiktok } from "react-icons/fa";
import { getAboutSEO, validateSEOData, generateKeywords } from "@/services/seoService";

const About = () => {
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  // Generate SEO data for About page
  const seoData = validateSEOData(getAboutSEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {seoData.keywords && (
          <meta name="keywords" content={generateKeywords(seoData.keywords)} />
        )}
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoData.url} />
        
        {/* Structured Data - JSON-LD Schema */}
        {seoData.schema && seoData.schema.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
      
    <div 
      className="flex flex-col"
      style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
    >
      <main className="flex-grow relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
          
          {/* Triangle Decorative Elements */}
          <div className="absolute top-1/4 right-10 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[35px] border-l-transparent border-r-transparent border-b-jamaica-green/10 animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/3 left-16 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[26px] border-l-transparent border-r-transparent border-b-jamaica-yellow/15 animate-bounce delay-1200"></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(0, 116, 47, 0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 116, 47, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Enhanced Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                  <Heart className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-4 py-1.5 rounded-2xl border border-jamaica-green/20 shadow-lg">
                <span className="text-sm font-bold text-jamaica-black flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-jamaica-green" />
                  Meet the Founders
                </span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
              <span className="bg-gradient-to-r from-gray-900 via-jamaica-green to-gray-900 bg-clip-text text-transparent px-2">
                About JamPack'd
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
              Bringing the authentic taste of 
              <span className="text-jamaica-yellow font-semibold"> Jamaica</span> to your doorstep, 
              <span className="text-jamaica-green font-semibold"> one care package at a time</span>
            </p>
          </motion.div>
        </div>
        
        {/* Main Content */}
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            {/* Paul's Story Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="relative bg-white/80 backdrop-blur-sm border-0 shadow-xl overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
                
                <CardContent className="relative p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg">
                      <span className="text-2xl">👋</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Hi, we’re Paul and Lois, and we built JamPack’d!</h2>
                      <div className="flex items-center gap-2 mt-1">
                        <MapPin className="h-4 w-4 text-jamaica-green" />
                        <span className="text-sm text-gray-600">Based in St. Thomas, Jamaica</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 leading-relaxed mb-6">
                      This might come as a surprise, but the JamPack’d team is just us! We handpick every snack, pack every box, run the website, and answer every email. Paul handles operations, logistics, and the website, while Lois leads our brand and marketing. We handle customer experience together from start to finish.
                    </p>

                    <p className="text-gray-700 leading-relaxed mb-6">
                      We built JamPack’d because we wanted to share a taste of home with anyone, anywhere in the world who misses Jamaica. That is why each JamPack’d box is jam packed with authentic treats and proudly packed in Jamaica, so you get the real deal, straight from the source. Our hope is that every unboxing brings you joy and reminds you of home, no matter how far away you are.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Mission Statement Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="relative bg-white/80 backdrop-blur-sm border-0 shadow-xl overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-r from-jamaica-yellow/5 via-transparent to-jamaica-green/5"></div>
                <div className="absolute top-0 left-0 w-28 h-28 bg-jamaica-green/10 rounded-full -translate-y-14 -translate-x-14"></div>
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-jamaica-yellow/10 rounded-full translate-y-10 translate-x-10"></div>
                
                <CardContent className="relative p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-jamaica-yellow to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Package className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">What Makes JamPack'd Special</h2>
                      <p className="text-sm text-gray-600">Authentic • Personal • Jamaican</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-jamaica-green/5 rounded-xl border border-jamaica-green/10">
                      <div className="w-12 h-12 bg-jamaica-green/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <span className="text-xl">🇯🇲</span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">Packed in Jamaica</h3>
                      <p className="text-sm text-gray-600">Every box is packed right here in Jamaica for maximum authenticity</p>
                    </div>
                    
                    <div className="text-center p-4 bg-jamaica-yellow/5 rounded-xl border border-jamaica-yellow/10">
                      <div className="w-12 h-12 bg-jamaica-yellow/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <span className="text-xl">🙋🏾‍♂️</span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">Personal Touch</h3>
                      <p className="text-sm text-gray-600">Handpicked by me with care and attention to quality</p>
                    </div>
                    
                    <div className="text-center p-4 bg-jamaica-green/5 rounded-xl border border-jamaica-green/10">
                      <div className="w-12 h-12 bg-jamaica-green/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <span className="text-xl">❤️</span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">Taste of Home</h3>
                      <p className="text-sm text-gray-600">Bringing joy and memories of Jamaica to your doorstep</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="relative bg-gradient-to-br from-jamaica-green/5 to-jamaica-yellow/5 border-0 shadow-xl backdrop-blur-sm overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
                
                <CardContent className="relative p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl mb-6 shadow-lg">
                    <Mail className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Let's Connect</h2>
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    Feel free to reach out to Paul at paul@jampackd.store or follow our pages on Instagram and TikTok @JamPackdCarePackages, where we share behind-the-scenes packing moments and new product updates.
                  </p>
                  <div className="flex flex-col items-center space-y-4">
                    <Button asChild className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-3 rounded-xl w-full max-w-xs">
                      <a href="mailto:paul@jampackd.store" className="flex items-center justify-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email Paul
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    </Button>
                    <div className="flex gap-3 w-full max-w-xs">
                      <Button asChild variant="outline" className="border-2 border-jamaica-green bg-white/80 backdrop-blur-sm text-jamaica-green hover:bg-jamaica-green/10 shadow-lg hover:shadow-xl transition-all duration-300 px-4 py-3 rounded-xl flex-1">
                        <a href="https://instagram.com/JamPackdCarePackages" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                          <Instagram className="h-4 w-4" />
                          <span className="hidden sm:inline">Instagram</span>
                        </a>
                      </Button>
                      <Button asChild variant="outline" className="border-2 border-jamaica-green bg-white/80 backdrop-blur-sm text-jamaica-green hover:bg-jamaica-green/10 shadow-lg hover:shadow-xl transition-all duration-300 px-4 py-3 rounded-xl flex-1">
                        <a href="https://tiktok.com/@JamPackdCarePackages" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                          <FaTiktok className="h-4 w-4" />
                          <span className="hidden sm:inline">TikTok</span>
                        </a>
                      </Button>
                    </div>
                  </div>
                  <div className="mt-6 text-sm">
                    {/* Mobile chips */}
                    <div className="grid grid-cols-1 gap-2 px-4 sm:hidden">
                      <div className="flex items-center gap-2 justify-center rounded-full border border-green-200 bg-green-50/60 text-gray-700 px-3 py-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="font-medium">Personal responses</span>
                      </div>
                      <div className="flex items-center gap-2 justify-center rounded-full border border-green-200 bg-green-50/60 text-gray-700 px-3 py-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="font-medium">Behind-the-scenes content</span>
                      </div>
                    </div>
                    {/* Desktop original layout */}
                    <div className="hidden sm:flex items-center justify-center gap-6 text-gray-500">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Personal responses</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Behind-the-scenes content</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </main>
    </div>
    </>
  );
};

export default About; 
